import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Sparkline } from "@/components/charts/sparkline";
import { cn } from "@/lib/utils";
import type { EvolutionCell, EvolutionMatrix, EvolutionRow } from "@/lib/analytics/types";

interface EvolutionMatrixProps {
  matrix: EvolutionMatrix;
}

/**
 * Returns the Tailwind text-color class for a delta value based on its amplitude.
 * Amplitude-based (4 bands) — sign is conveyed by the text label (+/-), not the colour.
 *
 * Bands:
 *   |Δ| < 10%          → text-muted-foreground  (negligible)
 *   10% ≤ |Δ| < 25%    → text-chart-2           (notable)
 *   25% ≤ |Δ| < 50%    → text-chart-5           (significant)
 *   |Δ| ≥ 50%           → text-destructive       (anomaly)
 */
function deltaColorClass(delta: EvolutionCell["delta"]): string {
  if (delta === null) return "";
  if (delta.kind === "new") return "text-chart-5";
  if (delta.kind === "gone") return "text-destructive";
  // ratio
  const abs = Math.abs(delta.value);
  if (abs < 0.1) return "text-muted-foreground";
  if (abs < 0.25) return "text-chart-2";
  if (abs < 0.5) return "text-chart-5";
  return "text-destructive";
}

/**
 * Formats a delta value as a display string.
 * Returns empty string for null (first bucket) and zero-zero cells.
 */
function formatDelta(delta: EvolutionCell["delta"]): string {
  if (delta === null) return "";
  if (delta.kind === "new") return "new";
  if (delta.kind === "gone") return "-100%";
  const pct = Math.round(delta.value * 100);
  return pct >= 0 ? `+${pct}%` : `${pct}%`;
}

function WorkflowNameCell({ row }: { row: EvolutionRow }) {
  const isSpecial = row.key === "other" || row.key === "total";
  return (
    <td
      className={cn(
        "sticky left-0 bg-background z-10 px-4 py-2 text-sm font-medium whitespace-nowrap",
        isSpecial && "font-semibold text-muted-foreground",
      )}
    >
      <span className="inline-block max-w-48 truncate align-middle" title={row.name}>
        {row.name}
      </span>
    </td>
  );
}

function BucketCell({ cell }: { cell: EvolutionCell }) {
  const deltaText = formatDelta(cell.delta);
  const colorClass = deltaColorClass(cell.delta);

  // Zero-zero cell: completely empty (no noise)
  const isZeroZero = cell.runs === 0 && cell.delta === null;

  return (
    <TableCell className="text-center px-3 py-2 min-w-20">
      {isZeroZero ? (
        <span className="text-muted-foreground/40 tabular-nums text-sm">—</span>
      ) : (
        <div className="flex flex-col items-center gap-0.5">
          <span className="tabular-nums text-sm font-medium">{cell.runs}</span>
          {deltaText ? (
            <span className={cn("text-xs tabular-nums", colorClass)}>{deltaText}</span>
          ) : null}
        </div>
      )}
    </TableCell>
  );
}

export function EvolutionMatrix({ matrix }: EvolutionMatrixProps) {
  const { periods, rows } = matrix;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Evolution matrix</CardTitle>
        <CardDescription>
          Runs per workflow per bucket — <span className="italic">%Δ vs previous bucket</span>.
          Sparklines normalized per row.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table className="text-sm w-full">
            <TableHeader>
              <TableRow className="sticky top-0 z-20 bg-background">
                {/* Workflow name header — also sticky left */}
                <TableHead className="sticky left-0 bg-background z-30 px-4 py-2 whitespace-nowrap min-w-40">
                  Workflow
                </TableHead>
                {periods.map((period) => (
                  <TableHead
                    key={period}
                    className="text-center px-3 py-2 whitespace-nowrap min-w-20 font-mono text-xs"
                  >
                    {period}
                  </TableHead>
                ))}
                <TableHead className="text-center px-3 py-2 whitespace-nowrap min-w-28 text-xs">
                  Trend
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={String(row.key)}
                  className={cn(
                    row.key === "total" && "border-t-2 border-border font-semibold bg-muted/30",
                    row.key === "other" && "bg-muted/10",
                  )}
                >
                  <WorkflowNameCell row={row} />
                  {row.cells.map((cell, ci) => (
                    <BucketCell key={ci} cell={cell} />
                  ))}
                  <TableCell className="text-center px-3 py-2">
                    <div className="flex justify-center">
                      <Sparkline data={row.sparklineData} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
